/** @jsx jsx */

/** @jsxFrag */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import throttle from 'lodash/throttle';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import EoiButtonsShared from 'bundles/degree-description/components/modules/expression-of-interest/EoiButtonsShared';
import type { EoiTextType } from 'bundles/degree-description/utils/EoiUtils';
import { getElementHeight } from 'bundles/degree-description/utils/utils';
import type { EoiButtonsType } from 'bundles/expression-of-interest/types';
import { setDriftChatWidgetYAxis } from 'bundles/expression-of-interest/utils/driftUtils';

type Props = {
  eoiButtons?: EoiButtonsType;
  eoiContainerText?: EoiTextType;
  refElement?: HTMLElement | null;
  currentNavRoute: string;
  trackingIndex?: number;
  setEoiTrackingIndex?: React.Dispatch<React.SetStateAction<number>>;
};

const styles = {
  container: (bottom: number) => css`
    background-color: var(--cds-color-feedback-background-informative-weak);
    bottom: ${bottom ?? -145}px;
    border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    display: flex;
    flex-direction: row-reverse;
    left: 0;
    padding: var(--cds-spacing-200);
    position: fixed;
    transition: bottom 0.3s;
    width: 100%;
    z-index: 6;

    > :not(:first-child) {
      margin-right: var(--cds-spacing-200);
    }

    a,
    button {
      margin-bottom: var(--cds-spacing-200);
    }

    ${breakpoints.down('xs')} {
      a,
      button {
        font-size: 14px;
        margin-bottom: 0;
        padding: var(--cds-spacing-150) var(--cds-spacing-100);
      }
    }
  `,
};

const EoiButtonsMobileFooter = ({
  eoiButtons,
  eoiContainerText,
  refElement,
  currentNavRoute,
  trackingIndex,
  setEoiTrackingIndex,
}: Props) => {
  const isMobileView = useMediaQuery(breakpoints.down('sm'));
  const [containerBottom, setContainerBottom] = useState<number>(-300);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const shouldShowFooterRef = useRef<boolean>(true);
  const [isOneTrustBannerVisible, setIsOneTrustBannerVisible] = useState<boolean>(false);
  /* minimum height to show eoi buttons footer,
   * if window innerHeight is less than this, hide the footer for page content visibility */
  const minHeightToShowFooter = 400;

  useEffect(() => {
    const wrapperRefHeight = getElementHeight(wrapperRef.current);

    if (isMobileView) {
      if (currentNavRoute !== 'overview') {
        setContainerBottom(0);
        setDriftChatWidgetYAxis(24 + wrapperRefHeight);
      } else {
        setContainerBottom(-wrapperRefHeight);
        setDriftChatWidgetYAxis(24);
      }
    }
  }, [isMobileView, currentNavRoute]);

  useEffect(() => {
    const refElementHeight = getElementHeight(refElement);
    const wrapperRefHeight = getElementHeight(wrapperRef.current);

    const onScrollHandler = throttle(() => {
      // if window innerHeight doesn't have enough space, hide sticky footer
      if (!shouldShowFooterRef.current) {
        setContainerBottom(-wrapperRefHeight);
        setDriftChatWidgetYAxis(0);
        return;
      }

      if (isMobileView) {
        let scrollYTrack = 0;
        if (refElementHeight) {
          scrollYTrack = refElement?.getBoundingClientRect().bottom ?? 0;
        } else {
          scrollYTrack = 0 - window.scrollY;
        }

        if (isOneTrustBannerVisible && scrollYTrack < 115) {
          setContainerBottom(195);
        } else if (scrollYTrack < 115) {
          setContainerBottom(0);
          setDriftChatWidgetYAxis(24 + wrapperRefHeight);
        } else if (scrollYTrack >= 0 && containerBottom >= 0) {
          setContainerBottom(-wrapperRefHeight);
          setDriftChatWidgetYAxis(24);
        }
      } else {
        const totalPageHeight = document.documentElement.scrollHeight;
        const scrollPosition = window.scrollY + window.innerHeight;
        // This moves the position of drift widget when we are at the bottom of the page to avoid blocking social icons
        if (scrollPosition >= totalPageHeight) {
          setDriftChatWidgetYAxis(74);
        } else {
          setDriftChatWidgetYAxis(24);
        }
      }
    }, 200);
    window.addEventListener('scroll', onScrollHandler);
    return () => {
      window.removeEventListener('scroll', onScrollHandler);
    };
  }, [containerBottom, isMobileView, refElement, isOneTrustBannerVisible]);

  useEffect(() => {
    // check window innerHeight on element mounted and register resize handler
    if (window.innerHeight <= minHeightToShowFooter) {
      shouldShowFooterRef.current = false;
    }

    const onResizeHandler = throttle(() => {
      if (window.innerHeight <= minHeightToShowFooter) {
        shouldShowFooterRef.current = false;
      } else {
        shouldShowFooterRef.current = true;
      }
    }, 200);
    window.addEventListener('resize', onResizeHandler);
    return () => {
      window.removeEventListener('resize', onResizeHandler);
    };
  }, []);

  useEffect(() => {
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const onTrustBannerElement = document.getElementById('onetrust-banner-sdk') as HTMLElement;
          if (onTrustBannerElement) {
            const computedStyle = window.getComputedStyle(onTrustBannerElement);
            const isBannerVisible = computedStyle.display !== 'none';
            setIsOneTrustBannerVisible(isBannerVisible);
            if (!isBannerVisible) {
              const refElementHeight = getElementHeight(refElement);
              let scrollYTrack = 0;
              if (refElementHeight) {
                scrollYTrack = refElement?.getBoundingClientRect().bottom ?? 0;
              } else {
                scrollYTrack = 0 - window.scrollY;
              }

              if (scrollYTrack !== 0 && scrollYTrack < 115) {
                setContainerBottom(0);
              }
            }
          }
        }
      }
    });

    const targetNode = document.documentElement;
    observer.observe(targetNode, { attributes: true, subtree: true });

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {eoiButtons && isMobileView && (
        <div data-testid="eoi-buttons-mobile-footer" css={styles.container(containerBottom)} ref={wrapperRef}>
          <EoiButtonsShared
            keySuffix="-eoi-overview-button"
            eoiButtons={eoiButtons}
            eoiContainerText={eoiContainerText}
            trackingNamePrefix="mobile_floating"
            trackingIndex={trackingIndex}
            setEoiTrackingIndex={setEoiTrackingIndex}
          />
        </div>
      )}
    </>
  );
};

export default EoiButtonsMobileFooter;
